'use client';

import { type MouseEventHandler, forwardRef, useCallback, useContext, useMemo } from 'react';

import { css, cx, splitResponsiveVariant } from '@/styled-system/css';

import { useControllableValue } from '../../hooks/use-controllable-value';
import { useDisabledInteractions } from '../../hooks/use-disabled-interactions';
import { Slot } from '../slot';
import { ToggleButtonGroupContext } from '../toggle-button-group/context/toggle-button-group.context';
import { ToggleButtonRootContext } from './context/toggle-button-root.context';
import { toggleButtonRecipe } from './styles/toggle-button-root.styles';
import type { ToggleButtonRootPropsInterface } from './types/toggle-button-root.types';

/**
 * @since 10.0.0
 */
export const ToggleButtonRoot = forwardRef<HTMLButtonElement, ToggleButtonRootPropsInterface>(
  (props: ToggleButtonRootPropsInterface, forwardedRef) => {
    const { value, loading = false, ...restProps } = props;
    const {
      value: contextValue,
      defaultValue: contextDefaultValue,
      setValue: setContextValue,
      size: contextSize,
      disabled: contextDisabled,
      fluid: contextFluid,
    } = useContext(ToggleButtonGroupContext);
    const isInToggleButtonGroup = setContextValue !== undefined;

    const memoPressed = useMemo(() => {
      if (value && isInToggleButtonGroup) {
        return Array.isArray(contextValue) ? contextValue?.includes(value) : value === contextValue;
      }
      return undefined;
    }, [value, contextValue, isInToggleButtonGroup]);

    const memoDefaultSelected = useMemo(() => {
      if (value && contextDefaultValue) {
        return Array.isArray(contextDefaultValue)
          ? contextDefaultValue?.includes(value)
          : value === contextDefaultValue;
      }
      return undefined;
    }, [value, contextDefaultValue]);

    const {
      asChild = false,
      children,
      className,
      css: cssProp,
      disabled = contextDisabled ?? false,
      fluid = contextFluid || false,
      size = contextSize || 'sm',
      selected: selectedProp,
      defaultSelected: defaultSelectedProp,
      onSelectedChange,
      onClick,
      ...rest
    } = restProps;

    const hasSelectedProp = 'selected' in props;
    const hasDefaultSelectedProp = 'defaultSelected' in props;
    const resolvedSelected = hasSelectedProp ? selectedProp : memoPressed;
    const resolvedDefaultSelected = hasDefaultSelectedProp
      ? defaultSelectedProp
      : memoDefaultSelected;

    const [selectedState, setSelectedState] = useControllableValue<boolean | undefined>({
      controlledValue: resolvedSelected,
      uncontrolledValue: resolvedDefaultSelected,
      isControlled: hasSelectedProp || memoPressed !== undefined,
      isUncontrolled: hasDefaultSelectedProp,
      onChange: (newSelected) => {
        if (newSelected !== undefined) {
          onSelectedChange?.(newSelected);
        }
      },
      componentName: 'ToggleButton.Root',
      controlledPropName: 'selected',
      uncontrolledPropName: 'defaultSelected',
    });

    const isDisabled = disabled || loading;
    const responsiveFluid = splitResponsiveVariant('fluid', fluid);

    const updateContextValue = useCallback(() => {
      if (!setContextValue || !value) return;

      if (Array.isArray(contextValue)) {
        const valueIsInContext = contextValue?.includes(value);
        const newValue = valueIsInContext
          ? contextValue?.filter((v) => v !== value)
          : [...(contextValue || []), value];

        setContextValue(newValue);
      } else {
        setContextValue(value);
      }
    }, [value, contextValue, setContextValue]);

    const handleClick: MouseEventHandler<HTMLButtonElement> = useCallback(
      (e) => {
        if (!isDisabled) {
          onClick?.(e);
          updateContextValue();
          setSelectedState((prev) => !prev);
        }
      },
      [setSelectedState, isDisabled, onClick, updateContextValue],
    );

    const styles = css(
      toggleButtonRecipe.raw({
        disabled,
        size: contextSize || size,
        selected: !!selectedState,
        loading,
        ...responsiveFluid,
      }),
      cssProp,
    );
    const Component = asChild ? Slot : 'button';

    // Loading toggle buttons stay focusable so users don't lose their place.
    const loadingDisabledInteractionsProps = useDisabledInteractions({ focusable: true });

    // A disabled `asChild` element can't use the native `disabled` attribute, so
    // mark it `aria-disabled`, remove it from the tab order, and block interactions.
    const disabledInteractionsProps = useDisabledInteractions({ focusable: false });

    const rootContextValue = useMemo(
      () => ({
        disabled,
        size: contextSize || size,
        selected: selectedState,
        loading,
      }),
      [disabled, contextSize, size, selectedState, loading],
    );

    return (
      <ToggleButtonRootContext.Provider value={rootContextValue}>
        <Component
          ref={forwardedRef}
          aria-pressed={!!selectedState}
          data-c11n-component="ToggleButton.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', 'c11n_toggle-button__root', 'group', styles, className)}
          onClick={handleClick}
          disabled={!asChild && disabled ? true : undefined}
          type={asChild ? undefined : 'button'}
          {...rest}
          {...(asChild && disabled
            ? disabledInteractionsProps
            : loading
              ? loadingDisabledInteractionsProps
              : {})}
        >
          {children}
        </Component>
      </ToggleButtonRootContext.Provider>
    );
  },
);

ToggleButtonRoot.displayName = 'ToggleButton.Root';
